import { Button } from '@ui/button.tsx';
import { Clock, RefreshCw, Wrench } from 'lucide-react';
import maintenanceLogo from '@r/assets/images/fiddy/logo.svg';
import type { MaintenancePageReason } from '@r/lib/maintenance-error.ts';

type MaintenanceErrorPageProps = {
  onRetry?: () => void;
  /** 503 = maintenance plateforme ; 500 = incident serveur (whitelist). */
  reason?: MaintenancePageReason;
};

const BADGE_BY_REASON: Record<MaintenancePageReason, string> = {
  '503': 'Maintenance technique',
  '500': 'Erreur 500 • Maintenance technique'
};

/**
 * Page full-screen maintenance.
 * Texte et logo en dur (hors i18n). Utilisée au boot, au login et sur erreur routeur.
 */
export const MaintenanceErrorPage = ({
  onRetry = () => {
    window.location.reload();
  },
  reason = '500'
}: MaintenanceErrorPageProps) => {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#f4f4f5] px-4 py-10">
      <img src={maintenanceLogo} alt="Logo" className="object-contain mb-8 w-auto h-9" />

      <div className="w-full max-w-[32rem] rounded-3xl border border-zinc-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)] sm:p-10">
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex gap-2 items-center px-3 py-1 mb-6 text-xs font-medium text-violet-700 bg-violet-100 rounded-full">
            <span aria-hidden className="size-1.5 rounded-full bg-violet-600" />
            {BADGE_BY_REASON[reason]}
          </span>

          <div
            aria-hidden
            className="flex justify-center items-center mb-6 text-violet-600 bg-violet-100 rounded-2xl size-16"
          >
            <Wrench className="size-8" strokeWidth={2.25} />
          </div>

          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 sm:text-[1.65rem] sm:leading-snug">
            Votre plateforme est momentanément indisponible
          </h1>

          <p className="mt-4 text-sm leading-relaxed text-zinc-500 sm:text-[0.9375rem]">
            Suite à un incident technique, votre plateforme ne peut pas être affichée pour le
            moment. Nos équipes sont mobilisées pour rétablir le service dans les plus brefs délais.
          </p>

          <div className="flex gap-2 justify-center items-center px-4 py-3 mt-6 w-full text-sm rounded-xl border border-zinc-100 bg-zinc-50 text-zinc-600">
            <Clock className="text-violet-600 size-4 shrink-0" aria-hidden />
            <span>Le service devrait être rétabli d&apos;ici quelques minutes</span>
          </div>

          <Button
            type="button"
            className="mt-8 w-full h-11 text-base font-medium text-white bg-violet-600 rounded-xl hover:bg-violet-700"
            onClick={onRetry}
          >
            <RefreshCw className="size-4" aria-hidden />
            Réessayez
          </Button>
        </div>
      </div>

      <p className="mt-10 max-w-md text-sm text-center text-zinc-500">
        Si le problème persiste, contactez notre support :{' '}
        <a
          href="mailto:support@nesso.pro"
          className="font-medium text-violet-600 underline underline-offset-2 hover:text-violet-700"
        >
          support@nesso.pro
        </a>
      </p>
    </div>
  );
};
